/**
 * Der Einstieg nach der Registrierung: /welcome
 *
 * DREI SCHRITTE (seit 05.11.2026, nach dem Signup-Wizard von Tappla TV):
 *
 *   1. Was willst du pruefen?      (Frage, fuettert „Dein Start in Claude“)
 *   2. Wen wollt ihr erreichen?    (Frage, dito)
 *   3. Mit Claude verbinden        (JetztDu, wartet live, bis Claude sich meldet)
 *
 * Danach „Fast fertig“: Hakt, Rolle und Quelle als Umfrage fuer uns, ganz zu
 * ueberspringen, dann /runs. Wie bei Tappla kommt die Umfrage nach dem
 * einen Schritt, ohne den nichts laeuft, nicht davor.
 *
 * ANLASS: Bis zum 05.11.2026 kamen fuenf Fragen, „Danke.“, vier Szenen, und
 * das Verbinden stand auf Bildschirm 11. Ein Testkunde fragte danach in
 * Claude, ohne 99takes hinzuzufuegen, und sah nicht, ob 99takes mitarbeitet.
 *
 * AUFBAU SEIT 05.11.2026 nach dem Onboarding von Higgsfield (Entwurf „Einstieg
 * nach Higgsfield“, Variante B „Vorschau links“, Design-Projekt „99takes —
 * Dashboard run.99takes.com“, gewaehlt von Robert): ab lg links eine dunkle
 * Flaeche ueber die ganze Hoehe, die zeigt, was die Antworten bringen
 * (Flaeche.tsx); rechts mittig die Frage mit zweizeiligem Titel, oben mittig
 * Striche statt Leiste (der laufende laenger), oben links ein runder
 * Zurueck-Knopf, unten mittig ein Satz, warum gefragt wird, und nach einer
 * Wahl die Antwort darauf (`reaktion`). Am Telefon steht die Flaeche als
 * Streifen ueber der Frage.
 *
 * KEIN UEBERSPRINGEN IN SCHRITT 1 UND 2 (wie bei Higgsfield). Die Umfrage
 * danach bleibt ganz zu ueberspringen, das fuehrt zum Ziel. Hinaus aus dem
 * Verbinden geht es nur bewusst, mit „Später verbinden“. Der Rundgang
 * (Einfuehrung.tsx) ist ein Abstecher vom Verbinden und fuehrt dorthin zurueck.
 * Der Zurueck-Knopf geht einen Bildschirm zurueck, auch zwischen Umfrage,
 * Verbinden und Frage 2. Beim Aendern der Antworten (?edit=1) alle fuenf am
 * Stueck, ohne Verbinden, mit „Abbrechen“.
 *
 * Gespeichert wird zweimal in dieselbe Zeile (upsert): nach Schritt 2, damit
 * niemand die Fragen noch einmal sieht, der beim Verbinden den Tab schliesst,
 * und nach der Umfrage. Jeder Bildschirm meldet `onboarding_step`.
 *
 * EINFACHAUSWAHL: DER KLICK IST DIE ANTWORT (seit 05.11.2026, vorher bewusst
 * nicht). Die Antworten sind deshalb Knoepfe, keine Radios: in einer
 * Radiogruppe waehlen die Pfeiltasten schon aus und haetten weitergeschaltet.
 * Hier bewegen die Pfeiltasten nur den Fokus, Klick oder Enter antwortet.
 * „Etwas anderes“ oeffnet das Textfeld, dort geht es mit „Weiter“.
 * Mehrfachauswahl: der Knopf ist grau („Wähle eine Option“), bis etwas
 * gewaehlt ist.
 *
 * Die Antworten erscheinen gestaffelt, eine Auswahl federt kurz (seit
 * 29.09.2026). Bei reduzierter Bewegung nur der Satz unten.
 *
 * Laesst sich nicht speichern (Tabelle fehlt, Netz weg), geht es trotzdem
 * weiter: niemand soll an einer Umfrage haengen bleiben.
 */

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ladeStatus, type Status } from '../api';
import {
  ablauf, anfang, ANDERES, FRAGEN, FREITEXT_MAX, fertig, frageKopf, frageReaktion, KERN, ladeOnboarding, optionText, speichereOnboarding, springe, zumSpeichern,
  type Aktion, type FrageId, type Gespeichert, type Stand,
} from '../onboarding';
import { ersterLaufVorschlag } from '../erster-lauf';
import { useSitzung } from '../session';
import { supabase } from '../supabase';
import { sicheresZiel } from '../ziel';
import { Kreisel, Wortmarke, ZweiZeilen } from '../ui';
import { CLAUDE_CONNECTORS, JetztDu, Rundgang, useVerbindungLive } from './Einfuehrung';
import { ANTWORT_BILDER, Flaeche, type FlaechenArt } from './Flaeche';
import { melde } from '../messung';
import { t } from '../sprache';

// ─── Einmal je Sitzung pruefen ──────────────────────────────────────────────
//
// Das Ergebnis haengt am Nutzer, nicht an der Seite: wer zwischen Konto und
// Laeufen wechselt, soll nicht jedes Mal eine Abfrage abwarten.

let bekannt: { nutzer: string; zustand: 'offen' | 'erledigt' | 'aus'; antworten?: Gespeichert } | null = null;

const merke = (nutzer: string, zustand: 'offen' | 'erledigt' | 'aus', antworten?: Gespeichert) => { bekannt = { nutzer, zustand, antworten }; };

/** Die Antworten dieser Sitzung, fuer „Dein erster Lauf". Ohne Antworten oder ohne Tabelle: nichts. */
export const bekannteAntworten = (nutzer: string | undefined): Gespeichert | null =>
  nutzer && bekannt?.nutzer === nutzer ? bekannt.antworten ?? null : null;

/** Fuer den Link auf dem Konto: nur zeigen, wenn sich Antworten speichern lassen. */
export const fragenVerfuegbar = (nutzer: string | undefined) => !!nutzer && bekannt?.nutzer === nutzer && bekannt.zustand !== 'aus';

/**
 * Vor die angemeldeten Seiten gestellt. Hat das Konto noch keine Zeile, geht
 * es einmal zu den Fragen; das Ziel wird mitgegeben. Fehlt die Tabelle, geht es
 * ohne Fragen weiter.
 */
export function MitFragen({ children }: { children: ReactNode }) {
  const { session } = useSitzung();
  const nutzer = session?.user?.id ?? session?.user?.email ?? '';
  const ort = useLocation();
  const [zustand, setZustand] = useState(bekannt?.nutzer === nutzer ? bekannt.zustand : null);

  useEffect(() => {
    if (!nutzer || bekannt?.nutzer === nutzer) return;
    let lebt = true;
    void ladeOnboarding(supabase).then(z => {
      merke(nutzer, z.zustand, z.zustand === 'erledigt' ? z.antworten : undefined);
      if (lebt) setZustand(z.zustand);
    });
    return () => { lebt = false; };
  }, [nutzer]);

  if (!zustand) {
    return (
      <main className="grid min-h-dvh place-items-center text-text-hell" role="status" aria-label={t('Einen Moment', 'One moment')}>
        <Kreisel className="size-6" />
      </main>
    );
  }
  if (zustand === 'offen') {
    return <Navigate to={`/welcome?next=${encodeURIComponent(ort.pathname + ort.search + ort.hash)}`} replace />;
  }
  return <>{children}</>;
}

// ─── Die Seite ──────────────────────────────────────────────────────────────

/** frage: eine Frage (Einstieg oder Umfrage). verbinden: Schritt 3 von 3. rundgang: der Abstecher vom Verbinden. */
type Phase = 'frage' | 'verbinden' | 'rundgang';

/** Die drei Schritte des Einstiegs: KERN Fragen und das Verbinden. */
const SCHRITTE = KERN + 1;

export default function Fragen() {
  const { session } = useSitzung();
  const nutzer = session?.user?.id ?? session?.user?.email ?? '';
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const aendern = params.get('edit') === '1';
  const naechstes = params.get('next');

  const [stand, setStand] = useState<Stand | null>(null);
  const [phase, setPhase] = useState<Phase>('frage');
  const [aus, setAus] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  /** Credits fuer den Verbinden-Schritt; ohne Zahl fehlt die Zeile. */
  const [guthaben, setGuthaben] = useState<number | null>(null);
  const status = useRef<Promise<Status | null> | null>(null);
  /** onboarding_done nur einmal, auch wenn jemand vom Verbinden zurueckgeht und neu abschliesst. */
  const gemeldet = useRef(false);
  const titel = useRef<HTMLHeadingElement>(null);
  /** Der kurze Halt nach einem Klick in der Einfachauswahl, bevor es weitergeht. */
  const sprung = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(sprung.current), []);

  // Dorthin, wo jemand hinwollte, sonst zum Standard /runs („Dein erster Run").
  const ziel = () => sicheresZiel(naechstes);
  // Wer aus claude.ai kommt (Ziel /connect), verbindet gerade schon: der
  // Schritt zeigt nur den Weg zur Freigabe, ohne Live-Pruefung und ohne Umfrage.
  const freigabe = sicheresZiel(naechstes).split(/[?#]/)[0] === '/connect';
  const verbunden = useVerbindungLive(!freigabe && !aendern && (phase === 'verbinden' || phase === 'rundgang'));

  useEffect(() => {
    // Die Konto-id brauchen wir erst beim Speichern; billing/status legt die
    // Kontozeile an, falls sie nach der Bestaetigung noch fehlt.
    status.current = ladeStatus().catch(e => { console.warn('[onboarding] Konto', e); return null; });
    let lebt = true;
    void ladeOnboarding(supabase).then(z => {
      if (!lebt) return;
      if (z.zustand === 'aus') { setAus(true); return; }
      setStand(anfang(z.zustand === 'erledigt' ? z.antworten : null));
    });
    return () => { lebt = false; };
  }, []);

  useEffect(() => {
    document.title = phase === 'frage'
      ? t('Ein paar Fragen · 99takes', 'A few questions · 99takes')
      : t('Mit Claude verbinden · 99takes', 'Connect to Claude · 99takes');
  }, [phase]);

  // Beim Wechsel der Frage den Titel fokussieren: ein Screenreader liest dann
  // die neue Frage vor, und Tab beginnt oben statt beim alten Knopf.
  useEffect(() => {
    if (stand && phase !== 'rundgang') titel.current?.focus({ preventScroll: true });
  }, [stand?.schritt, phase]);

  // Ein Ereignis je Bildschirm, wie onboarding_step im Tappla-Wizard: so sieht
  // GA4, wo Leute aussteigen. Nicht beim Aendern der Antworten.
  useEffect(() => {
    if (!stand || aendern || phase === 'rundgang') return;
    if (phase === 'verbinden') { melde({ event: 'onboarding_step', step: SCHRITTE, step_name: 'verbinden' }); return; }
    const i = Math.min(stand.schritt, FRAGEN.length - 1);
    melde({ event: 'onboarding_step', step: i < KERN ? i + 1 : i + 2, step_name: FRAGEN[i]!.id });
  }, [stand?.schritt, phase, aendern]);

  async function speichern(s: Stand, art: 'fertig' | 'uebersprungen'): Promise<Status | null> {
    setSpeichert(true);
    const s0 = await status.current;
    const id = s0?.account_id ?? null;
    const ok = id ? await speichereOnboarding(supabase, id, zumSpeichern(s), art) : false;
    // Ohne Speichern trotzdem weiter. Gemerkt wird dann „aus", damit die
    // Fragen in dieser Sitzung nicht noch einmal kommen.
    if (nutzer) merke(nutzer, ok ? 'erledigt' : 'aus', ok ? zumSpeichern(s) : undefined);
    setSpeichert(false);
    return s0;
  }

  /**
   * Nach Frage 2 (oder „Überspringen“ davor): speichern und zum Verbinden.
   * Gespeichert wird schon hier, damit niemand die Fragen noch einmal sieht,
   * der beim Verbinden den Tab schliesst. Die Umfrage danach ergaenzt dieselbe
   * Zeile per upsert.
   */
  async function zumVerbinden(s: Stand, art: 'fertig' | 'uebersprungen') {
    const s0 = await speichern(s, art);
    if (!gemeldet.current) { gemeldet.current = true; melde({ event: 'onboarding_done', art }); }
    setGuthaben(s0?.balance?.metered && typeof s0.balance.balance === 'number' ? s0.balance.balance : null);
    setStand(springe(s, KERN, 'vor'));
    setPhase('verbinden');
    window.scrollTo({ top: 0 });
  }

  /** Nach der Umfrage (oder ihrem „Überspringen“): speichern und zum Ziel. */
  async function abschliessen(s: Stand, art: 'fertig' | 'uebersprungen') {
    await speichern(s, art);
    navigate(ziel(), { replace: true });
  }

  /** Vom Verbinden weiter: in die Umfrage, oder bei der Freigabe gleich zurueck zu Claude. */
  function nachDemVerbinden() {
    if (freigabe) { navigate(ziel(), { replace: true }); return; }
    setStand(s => (s ? springe(s, KERN, 'vor') : s));
    setPhase('frage');
    window.scrollTo({ top: 0 });
  }

  if (aus) return <Navigate to={sicheresZiel(naechstes)} replace />;
  if (!stand) {
    return (
      <main className="grid min-h-dvh place-items-center text-text-hell" role="status" aria-label={t('Einen Moment', 'One moment')}>
        <Kreisel className="size-6" />
      </main>
    );
  }

  if (phase === 'rundgang') {
    return (
      <Rundgang
        zurueck={() => { setPhase('verbinden'); window.scrollTo({ top: 0 }); }}
        beiSzene={n => melde({ event: 'onboarding_step', step: SCHRITTE, step_name: `rundgang_${n}` })}
      />
    );
  }

  if (phase === 'verbinden') {
    // Vor der Verbindung: claude.ai in einem neuen Tab, diese Seite bleibt
    // stehen und wartet (useVerbindungLive). Danach „Weiter“ in die Umfrage.
    const aktion = freigabe
      ? <button type="button" onClick={nachDemVerbinden} className={`${KNOPF_GROSS} sm:w-auto`}>{t('Zur Freigabe', 'To approval')} <span aria-hidden>→</span></button>
      : verbunden
        ? <button type="button" onClick={nachDemVerbinden} autoFocus className={`${KNOPF_GROSS} sm:w-auto`}>{t('Weiter', 'Next')} <span aria-hidden>→</span></button>
        : (
          <a href={CLAUDE_CONNECTORS} target="_blank" rel="noreferrer" className={`${KNOPF_GROSS} no-underline sm:w-auto`}>
            {t('Claude öffnen', 'Open Claude')} <span aria-hidden>↗</span>
          </a>
        );
    const spaeter = !freigabe && !verbunden && (
      <button type="button" onClick={nachDemVerbinden} className={`whitespace-nowrap ${LEISE_KNOPF}`}>{t('Später verbinden', 'Connect later')}</button>
    );
    const label = t(`Schritt ${SCHRITTE} von ${SCHRITTE}`, `Step ${SCHRITTE} of ${SCHRITTE}`);
    return (
      <Buehne
        flaeche={<Flaeche art="claude" guthaben={guthaben} />}
        streifen={<Flaeche art="claude" klein />}
        oben={<Oben
          zurueck={() => { setStand(s => (s ? springe(s, KERN - 1, 'zurueck') : s)); setPhase('frage'); }}
          gesamt={SCHRITTE} gefuellt={SCHRITTE} aktiv={SCHRITTE - 1} label={label}
        />}
        leiste={
          <div className="flex items-center gap-3">
            {spaeter}
            <div className="flex-1 [&>*]:w-full">{aktion}</div>
          </div>
        }
      >
        <JetztDu
          titel={titel} guthaben={guthaben} freigabe={freigabe}
          verbunden={freigabe ? undefined : verbunden}
          rundgang={freigabe || verbunden ? undefined : () => { setPhase('rundgang'); window.scrollTo({ top: 0 }); }}
          aktion={aktion} spaeter={spaeter}
        />
      </Buehne>
    );
  }

  const tun = (a: Aktion) => setStand(s => (s ? ablauf(s, a) : s));
  const i = Math.min(stand.schritt, FRAGEN.length - 1);
  const frage = FRAGEN[i]!;
  const gewaehlt = stand.auswahl[frage.id] ?? [];
  const letzte = i === FRAGEN.length - 1;
  // Einstieg: die ersten KERN Fragen. Umfrage: der Rest, nach dem Verbinden.
  // Beim Aendern der Antworten (?edit=1) alle fuenf am Stueck, ohne Verbinden.
  const kern = !aendern && i < KERN;
  const umfrage = !aendern && i >= KERN;

  /** Von einem Stand aus weiter: nach Frage 2 zum Verbinden, nach der letzten zum Ziel. */
  function weiterVon(s: Stand) {
    if (speichert) return;
    const neu = ablauf(s, { typ: 'weiter' });
    if (kern && neu.schritt >= KERN) { void zumVerbinden(neu, 'fertig'); return; }
    if (fertig(neu)) { void abschliessen(neu, 'fertig'); return; }
    setStand(neu);
  }

  function weiter(e?: FormEvent) {
    e?.preventDefault();
    if (!stand || (!aendern && frage.mehrfach && gewaehlt.length === 0)) return;
    weiterVon(stand);
  }

  /**
   * Einfachauswahl: der Klick ist die Antwort (seit 05.11.2026, wie bei
   * Higgsfield und im Tappla-Wizard). Kurz stehen lassen, damit man sieht,
   * was man gewaehlt hat; bei reduzierter Bewegung sofort. „Etwas anderes“
   * braucht noch das Textfeld, dort geht es mit „Weiter“.
   */
  function antworten(wert: string) {
    if (!stand || speichert) return;
    const neu = ablauf(stand, { typ: 'waehlen', frage: frage.id, wert });
    setStand(neu);
    if (wert === ANDERES) return;
    window.clearTimeout(sprung.current);
    sprung.current = window.setTimeout(() => weiterVon(neu), ruhig() ? 0 : 220);
  }

  /**
   * Nur die Umfrage laesst sich ueberspringen (und das Aendern abbrechen),
   * beides fuehrt zum Ziel. Die zwei Fragen davor sind kurz und fuehren zum
   * Verbinden; Higgsfield zeigt dort auch kein Ueberspringen.
   */
  function ueberspringen() {
    if (!stand) return;
    void abschliessen(stand, 'uebersprungen');
  }

  function zurueckKnopf() {
    window.clearTimeout(sprung.current);
    if (umfrage && i === KERN) { setPhase('verbinden'); return; }
    tun({ typ: 'zurueck' });
  }

  // Enter auf einer Checkbox sendet sonst nichts ab.
  function taste(e: KeyboardEvent<HTMLFormElement>) {
    const feld = e.target as HTMLInputElement;
    if (e.key === 'Enter' && feld.type === 'checkbox') weiter(e);
  }

  // Was die Antworten bisher ergeben: derselbe Vorschlag wie in „Dein erster Run".
  const vorschlag = ersterLaufVorschlag(zumSpeichern(stand)).aufgaben[0]!;
  const flaeche: FlaechenArt = frage.id === 'zielgruppe' ? 'leute' : 'satz';

  const ueber = umfrage
    ? t(`Fast fertig · Frage ${i - KERN + 1} von ${FRAGEN.length - KERN}`, `Almost done · Question ${i - KERN + 1} of ${FRAGEN.length - KERN}`)
    : kern
      ? t(`Schritt ${i + 1} von ${SCHRITTE}`, `Step ${i + 1} of ${SCHRITTE}`)
      : t(`Frage ${i + 1} von ${FRAGEN.length}`, `Question ${i + 1} of ${FRAGEN.length}`);
  // Mehrfachauswahl: der Knopf ist da, aber grau, bis etwas gewaehlt ist.
  // Einfachauswahl: kein Knopf, ausser fuer „Etwas anderes“.
  const knopf = frage.mehrfach || gewaehlt.includes(ANDERES);
  const leer = !aendern && frage.mehrfach && gewaehlt.length === 0;
  const knopfText = speichert ? null
    : leer ? t('Wähle eine Option', 'Choose an option')
    : aendern ? (letzte ? t('Speichern', 'Save') : t('Weiter', 'Next'))
    : umfrage && letzte ? t('Zu deinem ersten Run', 'To your first run')
    : t('Weiter', 'Next');
  // Unten mittig ein Satz, warum gefragt wird; nach einer Wahl die Antwort darauf.
  const warum = umfrage
    ? t('Hilft uns, 99takes besser zu machen. Du kannst sie auch überspringen.', 'Helps us make 99takes better. You can also skip them.')
    : kern && i === 0
      ? t('Zwei Fragen, dann verbindest du 99takes mit Claude.', 'Two questions, then you connect 99takes to Claude.')
      : frageReaktion(frage);
  const fuss = gewaehlt.length > 0 ? frageReaktion(frage) : warum;
  const rechts = aendern || umfrage ? (
    <button
      type="button" onClick={ueberspringen} disabled={speichert}
      className="cursor-pointer rounded-md border-0 bg-transparent px-2 py-1.5 text-sm whitespace-nowrap text-text-leise underline decoration-linie underline-offset-[3px] transition-colors duration-150 hover:text-tinte hover:decoration-text-hell"
    >
      {aendern ? t('Abbrechen', 'Cancel') : t('Überspringen', 'Skip')}
    </button>
  ) : null;

  const zeile = (an: boolean) => `flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2 text-left text-[0.90625rem] leading-snug shadow-knopf transition-colors duration-150 sm:min-h-12 sm:text-[0.9375rem] ${
    an ? 'border-marke bg-marke/[0.07] font-medium text-tinte' : 'border-linie bg-flaeche text-text hover:border-linie-stark hover:bg-ebene'}`;
  const knopfInhalt = knopfText ?? <><Kreisel />{t('Einen Moment …', 'One moment …')}</>;

  return (
    <Buehne
      flaeche={<Flaeche art={flaeche} satz={vorschlag.satz} zurueck={vorschlag.zurueck} />}
      streifen={kern ? <Flaeche art={flaeche} satz={vorschlag.satz} klein /> : null}
      oben={<Oben
        zurueck={i > 0 ? zurueckKnopf : undefined}
        gesamt={aendern ? FRAGEN.length : SCHRITTE}
        gefuellt={aendern ? i + 1 : umfrage ? SCHRITTE : i + 1}
        aktiv={aendern || kern ? i : -1}
        label={ueber}
        rechts={rechts}
      />}
      fuss={fuss}
      leiste={
        <>
          {knopf && <button type="submit" form="frage" disabled={speichert || leer} className={leer ? KNOPF_LEER : KNOPF_GROSS}>{knopfInhalt}</button>}
          <p aria-live="polite" className={`m-0 text-center text-[0.75rem] leading-snug text-text-hell ${knopf ? 'mt-2' : ''}`}>{fuss}</p>
        </>
      }
    >
      <form id="frage" key={i} onSubmit={weiter} onKeyDown={taste}>
        {/* Nur der Inhalt gleitet herein, nicht das Formular: ein Vorfahr mit
            transform haelt die feste Leiste unten am Formular statt am
            Bildschirm, und „Weiter" lag bei 340 x 640 unter dem Rand. */}
        <div className={stand.richtung === 'vor' ? 'frage-vor' : 'frage-zurueck'}>
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="contents">
              <ZweiZeilen titel={titel} zeilen={frageKopf(frage)} />
            </legend>
            <p className="sr-only">{frage.mehrfach ? t('Mehrere Antworten möglich.', 'Several answers possible.') : t('Eine Antwort.', 'One answer.')}</p>

            {frage.mehrfach ? (
              <div className="mt-5 grid gap-1.5 sm:mt-7">
                {frage.optionen.map((o, n) => {
                  const an = gewaehlt.includes(o.wert);
                  return (
                    <label
                      key={o.wert}
                      style={{ animationDelay: `${120 + n * 40}ms` }}
                      className={`chip-rein ${an ? 'chip-an' : ''} ${zeile(an)} has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-marke`}
                    >
                      <input
                        type="checkbox" name={frage.id} value={o.wert} checked={an}
                        onChange={() => tun({ typ: 'waehlen', frage: frage.id, wert: o.wert })}
                        className="peer sr-only"
                      />
                      <Zeichenbild frage={frage.id} wert={o.wert} />
                      <span className="min-w-0 flex-1">{optionText(o)}</span>
                      <Haekchen an={an} rund={false} />
                    </label>
                  );
                })}
              </div>
            ) : (
              // Knoepfe statt Radios: in einer Radiogruppe waehlen die
              // Pfeiltasten schon aus, und ein Klick ist hier die Antwort.
              // Die Pfeiltasten bewegen nur den Fokus (`pfeile`).
              <ul className="m-0 mt-5 grid list-none gap-1.5 p-0 sm:mt-7" onKeyDown={pfeile}>
                {frage.optionen.map((o, n) => {
                  const an = gewaehlt.includes(o.wert);
                  return (
                    <li key={o.wert} style={{ animationDelay: `${120 + n * 40}ms` }} className={`chip-rein ${an ? 'chip-an' : ''}`}>
                      <button
                        type="button" aria-pressed={an} disabled={speichert}
                        onClick={() => antworten(o.wert)}
                        className={`${zeile(an)} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marke`}
                      >
                        <Zeichenbild frage={frage.id} wert={o.wert} />
                        <span className="min-w-0 flex-1">{optionText(o)}</span>
                        <Haekchen an={an} rund />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {gewaehlt.includes(ANDERES) && (
              <label className="mt-4 block">
                <span className="mb-1.5 block text-[0.84375rem] font-medium text-text">
                  {t('Was genau?', 'What exactly?')} <span className="font-normal text-text-hell">(optional)</span>
                </span>
                <input
                  value={stand.freitext[frage.id] ?? ''}
                  onChange={e => tun({ typ: 'freitext', frage: frage.id, text: e.target.value })}
                  maxLength={FREITEXT_MAX}
                  autoFocus={!frage.mehrfach}
                  className="box-border h-[2.75rem] w-full rounded-lg border border-linie-feld bg-flaeche px-3 text-[0.9375rem] text-text
                             shadow-[inset_0_1px_1px_rgb(20_18_12/0.04)] outline-none transition-[border-color,box-shadow] duration-150
                             hover:border-linie-stark focus:border-marke focus:shadow-[0_0_0_3px_rgb(63_138_135/0.16)] focus-visible:outline-none"
                />
              </label>
            )}
          </fieldset>

          {knopf && (
            <div className="mt-7 hidden justify-center sm:flex">
              <button type="submit" disabled={speichert || leer} className={`${leer ? KNOPF_LEER : KNOPF_GROSS} sm:w-auto sm:min-w-40`}>{knopfInhalt}</button>
            </div>
          )}
        </div>
      </form>
    </Buehne>
  );
}

/** Ohne Bewegung (Systemeinstellung): Antworten springen sofort weiter. */
const ruhig = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Pfeiltasten in der Liste der Einfachauswahl: Fokus zur naechsten oder vorigen Antwort, ohne zu waehlen. */
function pfeile(e: KeyboardEvent<HTMLUListElement>) {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const knoepfe = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('button')];
  const jetzt = knoepfe.indexOf(document.activeElement as HTMLButtonElement);
  if (jetzt < 0) return;
  e.preventDefault();
  const n = knoepfe.length;
  knoepfe[(jetzt + (e.key === 'ArrowDown' ? 1 : n - 1)) % n]!.focus();
}

const KNOPF_GROSS = 'inline-flex h-[2.75rem] w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-0 bg-knopf px-6 text-[0.9375rem] font-semibold text-auf-marke '
  + 'shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(20_18_12/0.18)] transition-[background-color,transform] duration-150 '
  + 'hover:bg-knopf-hover active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60';

/** Der Knopf, solange in einer Mehrfachauswahl nichts gewaehlt ist: grau, „Wähle eine Option“ (wie bei Higgsfield). */
const KNOPF_LEER = 'inline-flex h-[2.75rem] w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-linie bg-ebene px-6 text-[0.9375rem] font-semibold text-text-hell';

const LEISE_KNOPF = 'cursor-pointer rounded-md border-0 bg-transparent px-2 py-1.5 text-sm text-text-leise transition-colors duration-150 hover:bg-ebene hover:text-tinte';

/**
 * Der Rahmen jedes Bildschirms (Entwurf B „Vorschau links“, 05.11.2026): ab lg
 * links die Flaeche ueber die ganze Hoehe, rechts mittig der Bildschirm, unten
 * mittig ein Satz. Darunter eine Spalte mit der Flaeche als Streifen, und am
 * Telefon eine feste Leiste fuer Knopf und Satz.
 */
function Buehne({ flaeche, streifen, oben, fuss, leiste, children }: {
  flaeche: ReactNode;
  streifen: ReactNode;
  oben: ReactNode;
  fuss?: ReactNode;
  leiste: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[minmax(22rem,27.5rem)_minmax(0,1fr)]">
      <aside className="hidden lg:sticky lg:top-0 lg:block lg:h-dvh lg:p-3">{flaeche}</aside>
      <div className="relative flex min-h-dvh min-w-0 flex-col">
        {oben}
        <main className="mx-auto flex w-full max-w-[34rem] flex-1 flex-col overflow-x-clip px-4 pt-1 pb-36 sm:px-6 sm:pt-6 sm:pb-24 lg:justify-center lg:pt-0">
          {streifen && <div className="lg:hidden">{streifen}</div>}
          {children}
        </main>
        {fuss && <p aria-live="polite" className="absolute inset-x-6 bottom-6 m-0 hidden text-center text-[0.8125rem] text-text-hell sm:block">{fuss}</p>}
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-linie bg-grund/95 px-4 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:hidden">
          {leiste}
        </div>
      </div>
    </div>
  );
}

/**
 * Oben: links ein runder Zurueck-Knopf (auf dem ersten Bildschirm die
 * Wortmarke, ab lg steht sie in der Flaeche), mittig die Striche, rechts was
 * der Bildschirm braucht.
 */
function Oben({ zurueck, gesamt, gefuellt, aktiv, label, rechts }: {
  zurueck?: () => void;
  gesamt: number;
  gefuellt: number;
  /** Der laengere Strich; -1 fuer keinen (Umfrage: alle voll). */
  aktiv: number;
  label: string;
  rechts?: ReactNode;
}) {
  return (
    <header className="relative flex h-14 shrink-0 items-center justify-between gap-3 px-3 sm:h-16 sm:px-5">
      {zurueck ? (
        <button
          type="button" onClick={zurueck} aria-label={t('Zurück', 'Back')}
          className="grid size-9 cursor-pointer place-items-center rounded-full border border-linie bg-flaeche text-text shadow-knopf transition-colors duration-150 hover:border-linie-stark hover:text-tinte"
        >
          <svg aria-hidden viewBox="0 0 16 16" className="size-4"><path d="M10 3 5 8l5 5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      ) : (
        <Wortmarke className="h-5 lg:invisible" />
      )}
      <ol aria-label={label} className="absolute top-1/2 left-1/2 m-0 flex -translate-x-1/2 -translate-y-1/2 list-none gap-1.5 p-0">
        {Array.from({ length: gesamt }, (_, n) => (
          <li key={n} aria-current={n === aktiv ? 'step' : undefined}>
            <span className={`block h-1 rounded-full transition-[width,background-color] duration-300 ${n === aktiv ? 'w-7' : 'w-3.5'} ${n < gefuellt ? 'bg-marke' : 'bg-linie'}`} />
          </li>
        ))}
      </ol>
      <div className="flex min-w-9 justify-end">{rechts}</div>
    </header>
  );
}

/** Kaestchen (Mehrfach) oder Kreis (Einfach) rechts in der Antwort. */
function Haekchen({ an, rund }: { an: boolean; rund: boolean }) {
  return (
    <span
      aria-hidden
      className={`grid size-[1.125rem] shrink-0 place-items-center border-[1.5px] text-[0.6875rem] text-auf-marke transition-colors duration-150 ${rund ? 'rounded-full' : 'rounded-[5px]'} ${
        an
          ? (rund ? 'border-marke bg-flaeche shadow-[inset_0_0_0_4px_var(--color-marke)]' : 'border-knopf bg-knopf')
          : 'border-linie-stark bg-flaeche'}`}
    >
      {an && !rund ? '✓' : null}
    </span>
  );
}

/**
 * Links in der Antwort: bei „Was willst du pruefen?“ ein Zeichen, bei „Wen
 * wollt ihr erreichen?“ Portraets (KI-generiert, wie in der Flaeche). Die
 * Zeichen sind Strichzeichnungen nach Lucide (ISC-Lizenz).
 */
function Zeichenbild({ frage, wert }: { frage: FrageId; wert: string }) {
  if (frage === 'zielgruppe') {
    const bilder = ANTWORT_BILDER[wert];
    if (!bilder) return <span aria-hidden className="grid size-7 shrink-0 place-items-center rounded-full bg-ebene text-[0.8125rem] font-bold text-text-hell">…</span>;
    return (
      <span aria-hidden className="flex shrink-0">
        {bilder.map((b, n) => <img key={b} src={b} alt="" width={240} height={240} className={`size-7 rounded-full border-2 border-flaeche bg-linie object-cover ${n ? '-ml-3' : ''}`} />)}
      </span>
    );
  }
  const pfade = frage === 'pruefen' ? ZEICHEN[wert] : undefined;
  if (!pfade) return null;
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-[1.125rem] shrink-0 text-marke-tief" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      {pfade.map(d => <path key={d} d={d} />)}
    </svg>
  );
}

const ZEICHEN: Record<string, string[]> = {
  landingpages: ['M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z', 'M2 9h20', 'M6 4v5'],
  anzeigen: ['m3 11 18-5v12L3 14v-3z', 'M11.6 16.8a3 3 0 1 1-5.8-1.6'],
  emails: ['M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z', 'm22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7'],
  angebote: ['M12.59 2.59A2 2 0 0 0 11.17 2H4a2 2 0 0 0-2 2v7.17a2 2 0 0 0 .59 1.42l8.7 8.7a2.43 2.43 0 0 0 3.42 0l6.58-6.58a2.43 2.43 0 0 0 0-3.42z', 'M7.5 7.5h.01'],
  positionierung: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z', 'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z'],
  alles: ['m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z', 'm22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65', 'm22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65'],
};
